import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DataTable, ItiLink as Link } from 'components';
import { DataTableColumn } from 'components/tables/data-table';
import { useAuth } from 'contexts';
import { fetchPaginated } from 'services';
import { downloadCsv } from 'services/download-csv';
import { CarrierContact, Role } from 'types';
import { FilterOperator, FilterState, LogicalOperator } from 'utils';

const AllCarriers: FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [showDeleted, setShowDeleted] = useState<boolean>(true);

  const columns: DataTableColumn[] = [
    {
      field: 'id',
      label: 'RECORD',
      width: 80,
      renderCell: value => <Link to={`/scac-management/carriers/${value}`}>{value}</Link>
    },
    {
      field: 'scacCode',
      label: 'SCAC',
      width: 80
    },
    {
      field: 'name',
      label: 'NAME',
      width: 80
    },
    {
      field: 'usdotNumber',
      label: 'USDOT',
      width: 80
    },
    {
      field: 'deleted',
      label: 'STATUS',
      width: 80,
      renderCell: value => <p>{value ? 'Deactivated' : 'Active'}</p>
    },
    {
      field: 'contacts',
      label: 'CONTACT(S)',
      width: 80,
      renderCell: (contacts: CarrierContact[]) => <p>{!!contacts && contacts.length > 0 ? 'YES' : 'NO'}</p>
    }
  ];

  const initialFilters: FilterState = useMemo(() => {
    return {
      logicalOperator: LogicalOperator.AND,
      filters: showDeleted ? [] : [{ field: 'deleted', filterOperator: FilterOperator.EQUALS, value: false }]
    };
  }, [showDeleted]);

  return (
    <DataTable
      key={initialFilters.filters.length}
      fetchData={fetchPaginated('trucking-companies/page')}
      fetchCsv={downloadCsv('trucking-companies')}
      initialColumns={columns.map(c => ({ field: c.field, width: c.width }))}
      initialFilters={initialFilters}
      initialPage={{ sortField: 'id', sortDirection: 'asc', pageSize: 25 }}
      columns={columns}
      filterByLocation
      searchAdornment={
        <div className="d-flex align-items-center g-3">
          <label className="text-info-dark">SHOW DEACTIVATED</label>
          <label className="switch">
            <input type="checkbox" checked={showDeleted === true} onChange={e => setShowDeleted(e.target.checked)} />
            <span className="slider round" />
          </label>
          {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.ADMIN_CUSTOMER].includes(userProfile.role) && (
            <button
              id="create-carrier"
              className="btn-primary"
              onClick={() => navigate('/scac-management/carriers/new')}
              data-cy="new-carrier-button"
            >
              NEW CARRIER
            </button>
          )}
        </div>
      }
    />
  );
};

export default AllCarriers;
